import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import {
  formatDateTime,
  formatRelativeLastSeen,
  formatShortId,
  formatUserDisplayName,
  maskEmailAddress,
} from '../format';
import type { BackofficeUserRow, SortDirection, UserSortBy } from '../types';

const SORTABLE_HEADERS: Record<UserSortBy, string> = {
  name: 'Name',
  lastSeenOnlineAt: 'Last seen online',
  createdAt: 'Created',
};

const MOBILE_SORT_OPTIONS = [
  { value: 'name', label: SORTABLE_HEADERS.name },
  { value: 'lastSeenOnlineAt', label: SORTABLE_HEADERS.lastSeenOnlineAt },
  { value: 'createdAt', label: SORTABLE_HEADERS.createdAt },
] satisfies Array<{ value: UserSortBy; label: string }>;

export interface UsersTableProps {
  users: BackofficeUserRow[];
  sortBy: UserSortBy;
  sortDirection: SortDirection;
  showOrganizationColumn?: boolean;
  onSortChange: (sortBy: UserSortBy) => void;
}

function formatTier(value: string | null): string {
  return value ? value.replace('_', ' ') : 'unknown';
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: UserSortBy;
  sortBy: UserSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: UserSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function UsersTable({
  users,
  sortBy,
  sortDirection,
  showOrganizationColumn = true,
  onSortChange,
}: UsersTableProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div>
        <MobileSortControl
          label="Sort users"
          options={MOBILE_SORT_OPTIONS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {users.map((user) => {
            const displayName = formatUserDisplayName(user);
            const maskedEmail = maskEmailAddress(user.email);
            const detailPath = `/users/${user.id}`;

            return (
              <article
                key={user.id}
                role="button"
                tabIndex={0}
                aria-label={`Open ${displayName}`}
                className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <div className="min-w-0 text-body font-semibold text-neutral-900">
                  {displayName}
                </div>
                <div className="mt-1 min-w-0 break-all text-small text-neutral-600">
                  {maskedEmail}
                </div>
                <div className="mt-2 text-small text-neutral-600">
                  {user.numberProjects} {user.numberProjects === 1 ? 'project' : 'projects'}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className={[
          'w-full border-collapse text-left',
          showOrganizationColumn ? 'min-w-[1320px]' : 'min-w-[1060px]',
        ].join(' ')}
      >
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="w-12 px-4 py-3">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.name}
                field="name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3 font-semibold text-neutral-600">
              Email
            </th>
            {showOrganizationColumn && (
              <th scope="col" className="min-w-[260px] px-4 py-3 font-semibold text-neutral-600">
                Organization
              </th>
            )}
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              Number projects
            </th>
            <th scope="col" className="min-w-[190px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.lastSeenOnlineAt}
                field="lastSeenOnlineAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.createdAt}
                field="createdAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="w-14 px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {users.map((user) => {
            const displayName = formatUserDisplayName(user);
            const maskedEmail = maskEmailAddress(user.email);
            const detailPath = `/users/${user.id}`;
            const lastSeenOnline = formatRelativeLastSeen(user.lastSeenOnlineAt);

            return (
              <tr
                key={user.id}
                className="cursor-pointer hover:bg-backoffice-50 focus-within:bg-backoffice-50"
                tabIndex={0}
                aria-label={`Open ${displayName}`}
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    aria-label={`Select ${displayName}`}
                    className="h-4 w-4 rounded border-neutral-300 text-lavender-600"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium tabular-nums text-neutral-700">
                      {formatShortId(user.id)}
                    </span>
                    <CopyIdButton value={user.id} label="Copy user ID" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{displayName}</span>
                    <CopyIdButton value={displayName} label="Copy name" />
                  </div>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span>{maskedEmail}</span>
                    <CopyIdButton value={user.email} label="Copy email" />
                  </div>
                </td>
                {showOrganizationColumn && (
                  <td className="px-4 py-4 text-neutral-700">
                    {user.organizationId && user.organizationName ? (
                      <div className="group inline-flex items-center gap-2">
                        <Link
                          to={`/organizations/${user.organizationId}`}
                          className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          {user.organizationName}{' '}
                          <span className="text-neutral-500">
                            ({formatTier(user.organizationPricingTier)})
                          </span>
                        </Link>
                        <CopyIdButton value={user.organizationId} label="Copy organisation ID" />
                      </div>
                    ) : (
                      'No organisation'
                    )}
                  </td>
                )}
                <td className="px-4 py-4 tabular-nums text-neutral-700">{user.numberProjects}</td>
                <td className="px-4 py-4 text-neutral-700">
                  <span
                    title={
                      lastSeenOnline.suffix ? formatDateTime(user.lastSeenOnlineAt) : undefined
                    }
                  >
                    {[lastSeenOnline.value, lastSeenOnline.suffix].filter(Boolean).join(' ')}
                  </span>
                </td>
                <td className="px-4 py-4 text-neutral-700">{formatDateTime(user.createdAt)}</td>
                <td className="px-4 py-4">
                  <button
                    type="button"
                    className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-backoffice-50"
                    aria-label={`Actions for ${displayName}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
